import { useEffect, useRef, useState } from "react";
import { ApiError, fetchFastApi } from "../api/fastapi";
import {
  MatchItemStatus,
  MatchStatusItem,
  MatchStatusResponseSchema,
  OptroMatchPartialEnqueueSchema,
  OptroWorkpaperFile,
  StartMatchWorkpapersAgenticResponseSchema,
} from "../models/audit/optroSync";
import { fetchMatchesChunked } from "../lib/optroWorkpaperMatches";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_INTERVAL_MS = 30000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
// Floor for `maxTotalPolls` below — at a flat 2s cadence, 150 polls is ~5
// minutes. Small selections (or a single control) still get this much
// budget even though `5 polls/control` alone would work out to less.
const MIN_TOTAL_POLLS = 150;
// Polls "per control" budgeted into `maxTotalPolls` below: at the 2s flat
// cadence, 5 polls/control is ~10s of polling budget per control before the
// ceiling can kick in, before any failure backoff extends that further.
const POLLS_PER_REF = 5;

// Computes the independent ceiling on the total number of polls for a single
// job, regardless of backoff or `isDone`. Proportionate to selection size
// (`refCount * POLLS_PER_REF`, i.e. ~10s of polling budget per control at the
// flat 2s cadence) with a `MIN_TOTAL_POLLS` (~5 minutes) floor so small
// selections aren't cut short. With failure backoff (up to 30s/poll) actual
// wall-clock time can exceed this budget, which is fine — this exists purely
// as defense-in-depth so a job whose `isDone` never flips true (a backend
// bug, an expected-ref that can never resolve, etc.) can't poll forever. When
// exceeded, polling stops and every still-pending ref is resolved via
// `resolveStuckAsNoMatch`, same as the existing `isDone` / consecutive-failure
// exits.
function computeMaxTotalPolls(refCount: number): number {
  return Math.max(MIN_TOTAL_POLLS, Math.ceil(refCount * POLLS_PER_REF));
}

export interface MatchJobResult {
  status: MatchItemStatus;
  workpapers: OptroWorkpaperFile[];
  rationale: string | null;
}

type MatchJobResults = Record<string, MatchJobResult>;

function queuedResultsFor(refs: string[]): MatchJobResults {
  const results: MatchJobResults = {};
  for (const ref of refs) {
    results[ref] = { status: "queued", workpapers: [], rationale: null };
  }
  return results;
}

// Layers `items` over `base`, keyed by ref — never replaces the whole map,
// since (per the backend contract) a status poll's `items` only ever covers
// refs that were actually enqueued, not refs resolved from the POST's
// `cached` list nor refs that failed to enqueue.
function mergeItems(
  base: MatchJobResults,
  items: MatchStatusItem[],
): MatchJobResults {
  const next = { ...base };
  for (const item of items) {
    next[item.controlExternalRef] = {
      status: item.status,
      workpapers: item.workpapers,
      rationale: item.rationale,
    };
  }
  return next;
}

// Resolves any ref still stuck in queued/crawling to no_match. Used once
// polling has nothing left to tell us (isDone, or we gave up after
// consecutive failures) so rows a Hatchet run never reported on (e.g. a
// partial-enqueue failure) still land on a terminal, importable state.
function resolveStuckAsNoMatch(results: MatchJobResults): MatchJobResults {
  const next = { ...results };
  for (const ref of Object.keys(next)) {
    const current = next[ref];
    if (current.status === "queued" || current.status === "crawling") {
      next[ref] = { ...current, status: "no_match" };
    }
  }
  return next;
}

// Starts the agentic workpaper match job for the given refs ONCE PER MOUNT:
// `refs` is captured on the first render, and a later render passing a new
// array is deliberately ignored — the job identity is the mount. The sole
// call site (the review page) satisfies this by construction: the selection
// can only change on the Select route, so re-entering Review remounts the
// hook. A future call site whose refs change in place must remount via
// `key` (or this hook needs a restart-on-change effect).
// `cached` results from the start POST are merged in immediately; the rest
// are polled via the status endpoint using the `optroImportContext` backoff
// pattern (2s flat, exponential backoff on failure, capped at 30s, stop
// after 5 consecutive failures). Polling is skipped entirely when `cached`
// already covers every requested ref. If the start POST itself fails, falls
// back to the legacy chunked `POST /match-workpapers` flow so the review
// page still resolves matches (just without progressive per-doc status).
export function useOptroWorkpaperMatchJob(refs: string[]) {
  const [results, setResults] = useState<MatchJobResults>({});
  const [matching, setMatching] = useState(true);
  const [failed, setFailed] = useState(false);
  const refsRef = useRef(refs);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    setMatching(true);
    setFailed(false);
    setResults(queuedResultsFor(refsRef.current));

    const runLegacyFallback = async () => {
      await fetchMatchesChunked(refsRef.current, {
        isCancelled: () => cancelled,
        onProgress: (matches) => {
          if (cancelled) return;
          setResults((current) => {
            const next = { ...current };
            for (const match of matches) {
              next[match.controlExternalRef] = {
                status: match.workpapers.length > 0 ? "matched" : "no_match",
                workpapers: match.workpapers,
                rationale: null,
              };
            }
            return next;
          });
        },
      });
      if (!cancelled) setMatching(false);
    };

    const pollStatus = (matchId: string) => {
      const maxTotalPolls = computeMaxTotalPolls(refsRef.current.length);
      let consecutiveFailures = 0;
      let totalPolls = 0;

      const scheduleNext = (delayMs: number) => {
        if (cancelled) return;
        timeoutId = setTimeout(() => {
          void poll();
        }, delayMs);
      };

      const poll = async () => {
        totalPolls += 1;
        try {
          const response = await fetchFastApi({
            method: "GET",
            path: `/controls/external/match/${encodeURIComponent(matchId)}/status`,
          });
          if (cancelled) return;
          const parsed = MatchStatusResponseSchema.parse(response);
          consecutiveFailures = 0;
          setResults((current) => mergeItems(current, parsed.items));
          if (parsed.isDone) {
            setResults((current) => resolveStuckAsNoMatch(current));
            setMatching(false);
            return;
          }
          if (totalPolls >= maxTotalPolls) {
            console.error(
              "Workpaper match status poll exceeded maxTotalPolls without isDone; resolving stuck refs as no_match",
            );
            setFailed(true);
            setResults((current) => resolveStuckAsNoMatch(current));
            setMatching(false);
            return;
          }
          scheduleNext(POLL_INTERVAL_MS);
        } catch (error) {
          console.error("Failed to poll workpaper match status:", error);
          if (cancelled) return;
          consecutiveFailures += 1;
          if (
            consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES ||
            totalPolls >= maxTotalPolls
          ) {
            setFailed(true);
            setResults((current) => resolveStuckAsNoMatch(current));
            setMatching(false);
            return;
          }
          const backoffMs = Math.min(
            POLL_INTERVAL_MS * 2 ** consecutiveFailures,
            MAX_POLL_INTERVAL_MS,
          );
          scheduleNext(backoffMs);
        }
      };

      scheduleNext(POLL_INTERVAL_MS);
    };

    void (async () => {
      if (refsRef.current.length === 0) {
        setMatching(false);
        return;
      }

      try {
        const response = await fetchFastApi({
          method: "POST",
          path: "/controls/external/match-workpapers/agentic",
          body: { externalRefs: refsRef.current },
        });
        if (cancelled) return;
        const parsed =
          StartMatchWorkpapersAgenticResponseSchema.parse(response);
        setResults((current) => mergeItems(current, parsed.cached));

        const cachedRefs = new Set(
          parsed.cached.map((item) => item.controlExternalRef),
        );
        const allCached = refsRef.current.every((ref) => cachedRefs.has(ref));
        if (allCached) {
          setMatching(false);
          return;
        }
        pollStatus(parsed.matchId);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError) {
          const partial = OptroMatchPartialEnqueueSchema.safeParse(
            error.detailData,
          );
          if (partial.success && partial.data.enqueued > 0) {
            // Partial enqueue: some refs still got enqueued as Hatchet runs
            // and are worth polling for. Merge in `cached` FIRST — a ref
            // already resolved by a previous job must not be left "queued"
            // (seeded above) and later mislabeled no_match by
            // `resolveStuckAsNoMatch` just because some unrelated ref failed
            // to enqueue. The refs that genuinely failed to enqueue stay
            // "queued" and resolve to no_match once polling ends (isDone or
            // failure/poll-ceiling stop) via `resolveStuckAsNoMatch`.
            setResults((current) => mergeItems(current, partial.data.cached));
            pollStatus(partial.data.matchId);
            return;
          }
        }
        console.error(
          "Failed to start agentic workpaper match; falling back to legacy match:",
          error,
        );
        setFailed(true);
        void runLegacyFallback();
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  return { results, matching, failed };
}
